package com.havn.app.features.reports.presentation

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInHorizontally
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonBlock
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnWebRefreshAction
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.export.HavnXlsxMimeType
import com.havn.app.core.export.HavnXlsxSheet
import com.havn.app.core.export.buildHavnXlsx
import com.havn.app.core.localization.HavnStrings
import com.havn.app.core.localization.HavnLanguage
import com.havn.app.core.localization.LocalHavnLanguage
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnPickedFile
import com.havn.app.core.media.HavnAsyncImage
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnPropertyPhotoPreviewSource
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.presentation.AttachmentContactPickerMode
import com.havn.app.features.attachments.presentation.AttachmentRecipientFilter
import com.havn.app.features.attachments.presentation.AttachmentRecipientPickerScreen
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.reports.domain.PropertyReportFeatureInterest
import com.havn.app.features.reports.domain.PropertyReportOpenHouseInsight
import com.havn.app.features.reports.domain.SellerReport
import com.havn.app.features.reports.domain.SellerReportPreview
import com.havn.app.features.reports.domain.SellerReportStatus
import com.havn.app.ui.theme.LocalHavnThemeTokens
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.coroutines.delay

@Composable
fun SellerReportsScreen(
    viewModel: SellerReportsViewModel,
    onBackToToday: (() -> Unit)? = null,
    onOpenProperty: (String) -> Unit = {},
    agencyId: String? = null,
    agentName: String? = null,
    agencyName: String? = null,
    shareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    linkedShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    recentShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    isLoadingShareRecipients: Boolean = false,
    isLoadingMoreShareRecipients: Boolean = false,
    hasMoreShareRecipients: Boolean = false,
    isUploadingPdf: Boolean = false,
    onUploadReportPdf: (String, HavnPickedFile, (HavnAttachment) -> Unit, (String) -> Unit) -> Unit = { _, _, _, _ -> },
    onOpenReportPdf: (HavnAttachment) -> Unit = {},
    onSendReportEmail: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onSendReportSms: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onOpenShareRecipients: () -> Unit = {},
    onShareRecipientQueryChange: (String) -> Unit = {},
    onLoadMoreShareRecipients: () -> Unit = {},
    onRecordShareRecipients: (List<HavnAttachmentRecipient>) -> Unit = {},
    scrollToTopSignal: Int = 0
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val showRefreshIndicator = state.isLoading && when (state.mode) {
        SellerReportsMode.List -> state.reports.isNotEmpty()
        SellerReportsMode.Generate -> state.openHouses.isNotEmpty()
        SellerReportsMode.Preview -> state.preview != null
    }

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = SellerReportsMode::navigationDepth
    ) { mode ->
        HavnScreenFrame(
            eyebrow = "",
            title = when (mode) {
                SellerReportsMode.List -> strings.sellerReportsTitle
                SellerReportsMode.Generate -> strings.createReport
                SellerReportsMode.Preview -> strings.reportPreview
            },
            subtitle = "",
            backLabel = when (mode) {
                SellerReportsMode.List -> onBackToToday?.let { strings.navToday }
                SellerReportsMode.Generate -> strings.navReports
                SellerReportsMode.Preview -> strings.navReports
            },
            onBack = when (mode) {
                SellerReportsMode.List -> onBackToToday
                SellerReportsMode.Generate -> viewModel::openList
                SellerReportsMode.Preview -> viewModel::openList
            },
            isRefreshing = showRefreshIndicator,
            onRefresh = viewModel::refresh,
            scrollToTopSignal = if (mode == SellerReportsMode.List) scrollToTopSignal else 0,
            headerTrailing = if (mode == SellerReportsMode.Preview) {
                {
                    val reportId = state.preview?.reportId ?: state.selectedReportId
                    HavnWebRefreshAction(
                        onRefresh = {
                            if (reportId != null) {
                                viewModel.openReport(reportId)
                            } else {
                                viewModel.refresh()
                            }
                        },
                        isRefreshing = state.isLoading
                    )
                }
            } else null
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                state.error?.let { message ->
                    HavnCard {
                        Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyLarge)
                        Spacer(Modifier.height(10.dp))
                        HavnButton(
                            text = strings.commonDismiss,
                            onClick = viewModel::clearError,
                            style = HavnButtonStyle.Ghost,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                when (mode) {
                    SellerReportsMode.List -> SellerReportScreen(state, viewModel)
                    SellerReportsMode.Generate -> SellerReportGeneratorScreen(state, viewModel)
                    SellerReportsMode.Preview -> ReportPreviewScreen(
                        state = state,
                        viewModel = viewModel,
                        onOpenProperty = onOpenProperty,
                        agencyId = agencyId,
                        agentName = agentName,
                        agencyName = agencyName,
                        shareRecipients = shareRecipients,
                        linkedShareRecipients = linkedShareRecipients,
                        recentShareRecipients = recentShareRecipients,
                        isLoadingShareRecipients = isLoadingShareRecipients,
                        isLoadingMoreShareRecipients = isLoadingMoreShareRecipients,
                        hasMoreShareRecipients = hasMoreShareRecipients,
                        isUploadingPdf = isUploadingPdf,
                        onUploadReportPdf = onUploadReportPdf,
                        onOpenReportPdf = onOpenReportPdf,
                        onSendReportEmail = onSendReportEmail,
                        onSendReportSms = onSendReportSms,
                        onOpenShareRecipients = onOpenShareRecipients,
                        onShareRecipientQueryChange = onShareRecipientQueryChange,
                        onLoadMoreShareRecipients = onLoadMoreShareRecipients,
                        onRecordShareRecipients = onRecordShareRecipients
                    )
                }
            }
        }
    }
}

@Composable
fun SellerReportScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel
) {
    val strings = LocalHavnStrings.current
    var searchQuery by remember { mutableStateOf("") }
    var statusFilter by remember { mutableStateOf(ReportStatusFilter.All) }
    val filteredReports = remember(state.reports, searchQuery, statusFilter) {
        state.reports
            .filter { report -> statusFilter.matches(report) }
            .filter { report -> report.matchesSearch(searchQuery) }
    }
    var visibleReportCount by remember { mutableStateOf(0) }
    LaunchedEffect(filteredReports) {
        visibleReportCount = minOf(8, filteredReports.size)
        while (visibleReportCount < filteredReports.size) {
            delay(70)
            visibleReportCount = minOf(visibleReportCount + 4, filteredReports.size)
        }
    }

    if (state.isLoading && state.reports.isEmpty()) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            repeat(6) {
                SellerReportRowSkeleton()
            }
        }
    } else if (state.reports.isEmpty()) {
        EmptyState(
            title = strings.noSellerReportsTitle,
            body = strings.noSellerReportsBody,
            actionText = strings.navProperties,
            onAction = {}
        )
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            HavnSearchField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = strings.searchSellerReports
            )
            ReportFilters(
                selected = statusFilter,
                reports = state.reports,
                onSelected = { statusFilter = it }
            )
            if (filteredReports.isEmpty()) {
                EmptyState(
                    title = strings.noMatchingReports,
                    body = strings.tryDifferentSearchOrStatusFilter,
                    actionText = strings.clearFilters,
                    onAction = {
                        searchQuery = ""
                        statusFilter = ReportStatusFilter.All
                    }
                )
            } else {
                filteredReports.take(visibleReportCount).forEach { report ->
                    SavedReportRow(report = report, onClick = { viewModel.openReport(report.id) })
                }
            }
        }
    }
}

@Composable
private fun QuietSellerReportLoadingCard(label: String) {
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.46f).height(18.dp))
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.78f).height(12.dp))
            HavnSkeletonBlock(Modifier.fillMaxWidth(0.62f).height(12.dp))
        }
    }
}

@Composable
private fun SellerReportRowSkeleton() {
    HavnCard {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
            HavnSkeletonBlock(Modifier.size(58.dp), shape = MaterialTheme.shapes.medium)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.58f).height(15.dp))
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.76f).height(11.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    HavnSkeletonBlock(Modifier.width(56.dp).height(18.dp))
                    HavnSkeletonBlock(Modifier.width(64.dp).height(18.dp))
                    HavnSkeletonBlock(Modifier.width(48.dp).height(18.dp))
                }
            }
            HavnSkeletonBlock(Modifier.width(88.dp).height(28.dp), shape = MaterialTheme.shapes.medium)
        }
    }
}

@Composable
private fun SellerReportGeneratorScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.chooseOpenHouse, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (state.openHouses.isEmpty()) {
                Text(strings.noOpenHousesAvailable, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            } else {
                state.openHouses.forEach { openHouse ->
                    OpenHousePickerRow(
                        openHouse = openHouse,
                        selected = state.selectedOpenHouseId == openHouse.id,
                        onClick = { viewModel.selectOpenHouse(openHouse.id) }
                    )
                }
            }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.agentNotes, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            HavnTextField(
                value = state.agentNotes,
                onValueChange = viewModel::updateAgentNotes,
                label = strings.notesForSeller,
                placeholder = strings.sellerNotesPlaceholder,
                singleLine = false,
                leadingIcon = HavnIconKey.Mail
            )
            HavnButton(
                text = strings.generatePreview,
                onClick = viewModel::generatePreview,
                loading = state.isGenerating,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Chart
            )
        }
    }
}

@Composable
fun ReportPreviewScreen(
    state: SellerReportsUiState,
    viewModel: SellerReportsViewModel,
    onOpenProperty: (String) -> Unit,
    agencyId: String? = null,
    agentName: String? = null,
    agencyName: String? = null,
    shareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    linkedShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    recentShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    isLoadingShareRecipients: Boolean = false,
    isLoadingMoreShareRecipients: Boolean = false,
    hasMoreShareRecipients: Boolean = false,
    isUploadingPdf: Boolean = false,
    onUploadReportPdf: (String, HavnPickedFile, (HavnAttachment) -> Unit, (String) -> Unit) -> Unit = { _, _, _, _ -> },
    onOpenReportPdf: (HavnAttachment) -> Unit = {},
    onSendReportEmail: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onSendReportSms: (List<HavnAttachmentRecipient>, HavnAttachment) -> Unit = { _, _ -> },
    onOpenShareRecipients: () -> Unit = {},
    onShareRecipientQueryChange: (String) -> Unit = {},
    onLoadMoreShareRecipients: () -> Unit = {},
    onRecordShareRecipients: (List<HavnAttachmentRecipient>) -> Unit = {}
) {
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    val preview = state.preview
    if (preview == null) {
        if (state.isLoading) {
            QuietSellerReportLoadingCard(strings.openingPropertyReportLoading)
            return
        }
        if (state.isGenerating) {
            QuietSellerReportLoadingCard(strings.openingPropertyReportCreating)
            return
        }
        EmptyState(
            title = strings.noPreviewReadyTitle,
            body = strings.noPreviewReadyBody,
            actionText = strings.createReport,
            onAction = viewModel::openGenerator
        )
        return
    }
    val reportHistory = state.reports
        .filter { report ->
            preview.propertyId != null && report.propertyId == preview.propertyId
        }
    var generatedPdf by remember(preview.reportId, preview.propertyId) { mutableStateOf<GeneratedSellerReportExport?>(null) }
    var uploadedPdf by remember(preview.reportId, preview.propertyId) { mutableStateOf<HavnAttachment?>(null) }
    var shareError by remember(preview.reportId, preview.propertyId) { mutableStateOf<String?>(null) }
    var contactPickerMode by remember { mutableStateOf<AttachmentContactPickerMode?>(null) }
    var recipientQuery by remember { mutableStateOf("") }
    var recipientFilter by remember { mutableStateOf(AttachmentRecipientFilter.All) }
    var selectedRecipientIds by remember(contactPickerMode) { mutableStateOf(emptySet<String>()) }
    var confirmDelete by remember { mutableStateOf(false) }

    if (confirmDelete) {
        AlertDialog(
            onDismissRequest = { confirmDelete = false },
            title = { Text(strings.deleteReport) },
            text = { Text(strings.deleteReportBody) },
            confirmButton = {
                TextButton(
                    onClick = {
                        confirmDelete = false
                        viewModel.deletePreviewReport()
                    }
                ) {
                    Text(strings.commonDelete, color = LocalHavnThemeTokens.current.danger)
                }
            },
            dismissButton = {
                TextButton(onClick = { confirmDelete = false }) {
                    Text(strings.commonCancel)
                }
            }
        )
    }

    contactPickerMode?.let { mode ->
        Dialog(
            onDismissRequest = { contactPickerMode = null },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            Surface(color = LocalHavnThemeTokens.current.background, modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
                    AnimatedVisibility(
                        visible = true,
                        enter = slideInHorizontally(animationSpec = tween(260), initialOffsetX = { it }) + fadeIn(animationSpec = tween(220))
                    ) {
                        AttachmentRecipientPickerScreen(
                            mode = mode,
                            query = recipientQuery,
                            selectedFilter = recipientFilter,
                            selectedRecipientIds = selectedRecipientIds,
                            allRecipients = shareRecipients,
                            linkedRecipients = linkedShareRecipients,
                            recentRecipients = recentShareRecipients,
                            isLoading = isLoadingShareRecipients,
                            isLoadingMore = isLoadingMoreShareRecipients,
                            hasMoreRecipients = hasMoreShareRecipients,
                            onQueryChange = {
                                recipientQuery = it
                                onShareRecipientQueryChange(it)
                            },
                            onFilterChange = { recipientFilter = it },
                            onBack = { contactPickerMode = null },
                            onToggleRecipient = { id -> selectedRecipientIds = selectedRecipientIds.toggleReportRecipient(id) },
                            onLoadMoreRecipients = onLoadMoreShareRecipients,
                            onSend = { recipients ->
                                contactPickerMode = null
                                val pdf = generatedPdf
                                val attachment = uploadedPdf
                                shareError = when {
                                    pdf == null -> strings.pdfGenerateBeforeSending
                                    attachment == null -> strings.pdfUploadBeforeSharing
                                    mode == AttachmentContactPickerMode.Email -> {
                                        onSendReportEmail(recipients, attachment)
                                        null
                                    }
                                    mode == AttachmentContactPickerMode.Sms -> {
                                        onSendReportSms(recipients, attachment)
                                        null
                                    }
                                    else -> strings.pdfUploadBeforeSharing
                                }
                                if (shareError == null) onRecordShareRecipients(recipients)
                            }
                        )
                    }
                }
            }
        }
    }

    ExportReadyReportLayout(
        preview = preview,
        reportHistory = reportHistory,
        selectedReportId = preview.reportId,
        onSummaryChange = viewModel::updatePreviewSummary,
        onRecommendationsChange = viewModel::updatePreviewRecommendations,
        onNextStepsChange = viewModel::updatePreviewNextSteps,
        onStatusSelected = viewModel::updatePreviewStatus,
        onReportHistoryClick = viewModel::openReport,
        onGenerateAiInsights = { viewModel.generateAiInsights(language.aiLanguageInstruction()) },
        isGeneratingInsights = state.isGenerating,
        onOpenProperty = onOpenProperty
    )

    SellerReportPdfActions(
        preview = preview,
        generatedPdf = generatedPdf,
        uploadedPdf = uploadedPdf,
        isUploadingPdf = isUploadingPdf,
        shareError = shareError,
        onGenerate = {
            val pdf = preview.generateSellerReportXlsx(agentName, agencyName, strings, language)
            generatedPdf = pdf
            uploadedPdf = null
            shareError = null
            val propertyId = preview.propertyId
            if (propertyId == null) {
                shareError = strings.sellerReportPdfNeedsProperty
            } else {
                onUploadReportPdf(
                    propertyId,
                    HavnPickedFile(pdf.fileName, pdf.bytes, HavnXlsxMimeType),
                    { attachment ->
                        uploadedPdf = attachment
                        shareError = null
                    },
                    { message -> shareError = message }
                )
            }
        },
        onOpenPdf = { uploadedPdf?.let(onOpenReportPdf) },
        onSendEmail = {
            recipientQuery = ""
            onOpenShareRecipients()
            contactPickerMode = AttachmentContactPickerMode.Email
        },
        onSendSms = {
            recipientQuery = ""
            onOpenShareRecipients()
            contactPickerMode = AttachmentContactPickerMode.Sms
        }
    )

    HavnButton(
        text = strings.deleteReport,
        onClick = { confirmDelete = true },
        loading = state.isDeletingReport,
        style = HavnButtonStyle.Danger,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Trash
    )
}

@Composable
private fun ReportStatusSegment(
    selected: SellerReportStatus,
    onSelected: (SellerReportStatus) -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(
        horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap),
        modifier = Modifier.fillMaxWidth()
    ) {
        visibleSellerReportStatuses.forEach { status ->
            HavnListFilterChip(
                label = status.localizedLabel(strings),
                selected = selected == status,
                onClick = { onSelected(status) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ExportReadyReportLayout(
    preview: SellerReportPreview,
    reportHistory: List<SellerReport>,
    selectedReportId: String?,
    onSummaryChange: (String) -> Unit,
    onRecommendationsChange: (String) -> Unit,
    onNextStepsChange: (String) -> Unit,
    onStatusSelected: (SellerReportStatus) -> Unit,
    onReportHistoryClick: (String) -> Unit,
    onGenerateAiInsights: () -> Unit,
    isGeneratingInsights: Boolean,
    onOpenProperty: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val wide = havnIsWebShell && maxWidth >= 1040.dp
        if (wide && preview.propertyId != null) {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                PropertySellerReportMain(
                    preview = preview,
                    onGenerateAiInsights = onGenerateAiInsights,
                    isGeneratingInsights = isGeneratingInsights,
                    onStatusSelected = onStatusSelected,
                    onOpenProperty = onOpenProperty,
                    modifier = Modifier.weight(1f)
                )
                PropertySellerReportSidebar(
                    preview = preview,
                    reportHistory = reportHistory,
                    selectedReportId = selectedReportId,
                    onReportHistoryClick = onReportHistoryClick,
                    modifier = Modifier.widthIn(min = 280.dp, max = 340.dp)
                )
            }
        } else if (preview.propertyId != null) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                PropertySellerReportMain(
                    preview = preview,
                    onGenerateAiInsights = onGenerateAiInsights,
                    isGeneratingInsights = isGeneratingInsights,
                    onStatusSelected = onStatusSelected,
                    onOpenProperty = onOpenProperty,
                    modifier = Modifier.fillMaxWidth()
                )
                PropertySellerReportSidebar(
                    preview = preview,
                    reportHistory = reportHistory,
                    selectedReportId = selectedReportId,
                    onReportHistoryClick = onReportHistoryClick,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        } else {
            LegacyOpenHouseReportLayout(preview, onSummaryChange, onRecommendationsChange, onNextStepsChange)
        }
    }
}

@Composable
private fun PropertySellerReportMain(
    preview: SellerReportPreview,
    onGenerateAiInsights: () -> Unit,
    isGeneratingInsights: Boolean,
    onStatusSelected: (SellerReportStatus) -> Unit,
    onOpenProperty: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    BoxWithConstraints(modifier) {
        val wide = maxWidth >= 860.dp
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        SellerReportHeroCard(preview, wide, onOpenProperty)

        SellerReportKpiGrid(preview, wide)

        ReportStatusSegment(
            selected = preview.status,
            onSelected = onStatusSelected
        )

        if (wide) {
            Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Top) {
                PropertyFunnelCard(preview, Modifier.weight(0.85f))
                PropertyTimelineCard(preview.timeline, Modifier.weight(1.35f))
            }
        } else {
            PropertyFunnelCard(preview, Modifier.fillMaxWidth())
            PropertyTimelineCard(preview.timeline, Modifier.fillMaxWidth())
        }

        OpenHouseBreakdownCard(preview.timeline)

        AiInsightsJsonCard(
            preview = preview,
            onGenerate = onGenerateAiInsights,
            isLoading = isGeneratingInsights,
            modifier = Modifier.fillMaxWidth()
        )
        }
    }
}

@Composable
private fun PropertyReportHeroText(
    preview: SellerReportPreview,
    onOpenProperty: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(preview.propertyTitle.ifBlank { preview.propertyAddress }, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
        Text(preview.propertyAddress, color = theme.primaryAccent, style = MaterialTheme.typography.titleMedium)
        Text("${strings.sellerReportGeneratedPrefix} ${preview.reportGeneratedAt.compactDateTimeLabel()} ${strings.sellerReportGeneratedSuffix}", color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            SmallPill(preview.propertyPrice.ifBlank { strings.priceNotSet })
            SmallPill("${preview.propertyStatus} ${preview.daysActive} ${strings.daysSuffix}")
        }
        HavnButton(
            text = strings.viewProperty,
            onClick = { preview.propertyId?.let(onOpenProperty) },
            style = HavnButtonStyle.Secondary,
            enabled = preview.propertyId != null,
            modifier = if (havnIsWebShell) Modifier.widthIn(max = 240.dp) else Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Home
        )
    }
}

@Composable
private fun SellerReportHeroCard(
    preview: SellerReportPreview,
    wide: Boolean,
    onOpenProperty: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.card,
        shape = MaterialTheme.shapes.large,
        border = BorderStroke(1.dp, theme.border),
        tonalElevation = 2.dp,
        modifier = Modifier.fillMaxWidth()
    ) {
        if (wide) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                ReportPropertyPhoto(
                    preview.propertyImageDataUrl,
                    Modifier
                        .weight(1.15f)
                        .height(190.dp)
                        .clip(MaterialTheme.shapes.large)
                )
                PropertyReportHeroText(
                    preview,
                    onOpenProperty,
                    Modifier
                        .weight(1f)
                        .padding(24.dp)
                )
            }
        } else {
            Column {
                ReportPropertyPhoto(
                    preview.propertyImageDataUrl,
                    Modifier
                        .fillMaxWidth()
                        .height(180.dp)
                        .clip(MaterialTheme.shapes.large)
                )
                PropertyReportHeroText(preview, onOpenProperty, Modifier.padding(18.dp))
            }
        }
    }
}

@Composable
private fun SellerReportKpiGrid(preview: SellerReportPreview, wide: Boolean) {
    val strings = LocalHavnStrings.current
    if (wide) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            SellerReportKpiCard(HavnIconKey.Calendar, strings.connectedOpenings, preview.openHouseCount.toString(), Color(0xFFF6B84B), Modifier.weight(1f))
            SellerReportKpiCard(HavnIconKey.Leads, strings.commonInquiries, preview.inquiryCount.toString(), Color(0xFF64748B), Modifier.weight(1f))
            SellerReportKpiCard(HavnIconKey.Users, strings.visitors, preview.totalVisitors.toString(), Color(0xFF38BDF8), Modifier.weight(1f))
            SellerReportKpiCard(HavnIconKey.Leads, strings.interestedBuyers, preview.interestedBuyers.toString(), Color(0xFF7DD36F), Modifier.weight(1f))
            SellerReportKpiCard(HavnIconKey.Alert, strings.hotLeads, preview.hotLeads.toString(), Color(0xFFFF6B4A), Modifier.weight(1f))
            SellerReportKpiCard(HavnIconKey.Check, strings.completedFollowUps, preview.followUpsSent.toString(), Color(0xFF70D182), Modifier.weight(1.25f))
            SellerConfidenceKpiCard(preview, Modifier.weight(1.05f))
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                SellerReportKpiCard(HavnIconKey.Calendar, strings.connectedOpenings, preview.openHouseCount.toString(), Color(0xFFF6B84B), Modifier.weight(1f))
                SellerReportKpiCard(HavnIconKey.Leads, strings.commonInquiries, preview.inquiryCount.toString(), Color(0xFF64748B), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                SellerReportKpiCard(HavnIconKey.Users, strings.visitors, preview.totalVisitors.toString(), Color(0xFF38BDF8), Modifier.weight(1f))
                SellerReportKpiCard(HavnIconKey.Leads, strings.interestedBuyers, preview.interestedBuyers.toString(), Color(0xFF7DD36F), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                SellerReportKpiCard(HavnIconKey.Alert, strings.hotLeads, preview.hotLeads.toString(), Color(0xFFFF6B4A), Modifier.weight(1f))
                SellerReportKpiCard(HavnIconKey.Check, strings.completedFollowUps, preview.followUpsSent.toString(), Color(0xFF70D182), Modifier.weight(1f))
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                SellerConfidenceKpiCard(preview, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun SellerReportKpiCard(
    icon: HavnIconKey,
    label: String,
    value: String,
    accent: Color,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.height(126.dp),
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            HavnIcon(icon, contentDescription = label, tint = accent, modifier = Modifier.size(24.dp))
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
                Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, maxLines = 2, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

@Composable
private fun SellerConfidenceKpiCard(preview: SellerReportPreview, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = modifier.height(126.dp),
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Surface(
                modifier = Modifier.size(48.dp),
                color = Color.Transparent,
                shape = MaterialTheme.shapes.large,
                border = BorderStroke(4.dp, if (preview.sellerConfidenceScore >= 70) theme.primaryAccent else Color(0xFFF97316))
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(preview.sellerConfidenceScore.toString(), color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(strings.sellerConfidence, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, maxLines = 2, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

@Composable
private fun LegacyOpenHouseReportLayout(
    preview: SellerReportPreview,
    onSummaryChange: (String) -> Unit,
    onRecommendationsChange: (String) -> Unit,
    onNextStepsChange: (String) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            Column {
                val reportTitle = preview.reportTitle
                    .takeIf { it.isNotBlank() && it != "Seller Report" }
                    ?: strings.reportPreview
                Text(reportTitle, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                Text(preview.propertyAddress, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                Text(preview.openHouseDateTime, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                if (preview.isAiDraft) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        strings.aiDraftReviewBeforeSending,
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                ReportMetric(strings.commonVisitors, preview.totalVisitors.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonHot, preview.hotLeads.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonWarm, preview.warmLeads.toString(), Modifier.weight(1f))
                ReportMetric(strings.commonCold, preview.coldLeads.toString(), Modifier.weight(1f))
            }

            if (preview.sellerConfidenceScore > 0) {
                ReportMetric(strings.sellerConfidence, "${preview.sellerConfidenceScore}/100")
                ReportSection(strings.sellerConfidenceExplanation, preview.sellerConfidenceExplanation)
            }

            EditableReportSection(strings.executiveSummary, preview.summaryText, onSummaryChange)
            ReportSection(strings.buyerInterestOverview, preview.buyerSentimentSummary)
            ReportSection(strings.whatBuyersLoved, preview.mostLovedFeatures)
            ReportSection(strings.buyerConcerns, preview.commonConcerns)
            ReportSection(strings.leadQualitySummary, preview.leadQualitySummary.ifBlank { preview.summaryText })
            ReportSection(strings.agentActivity, preview.agentActivitySummary.ifBlank { "${preview.followUpsSent} ${strings.followUpsSent.lowercase()}" })
            EditableReportSection(strings.aiRecommendations, preview.recommendations, onRecommendationsChange)
            ReportSection(strings.marketPositionComparison, preview.marketPositionComparison)
            EditableReportSection(strings.nextSteps, preview.nextSteps, onNextStepsChange)
        }
    }
}

@Composable
private fun ReportMetric(label: String, value: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier) {
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        Text(value, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
    }
}

@Composable
private fun PropertySellerReportSidebar(
    preview: SellerReportPreview,
    reportHistory: List<SellerReport>,
    selectedReportId: String?,
    onReportHistoryClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(14.dp)) {
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(strings.dataRollup, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                SidebarMetric(HavnIconKey.Calendar, strings.connectedOpenings, preview.openHouseCount, Color(0xFF38BDF8))
                SidebarMetric(HavnIconKey.Leads, strings.commonInquiries, preview.inquiryCount, theme.primaryText)
                SidebarMetric(HavnIconKey.Users, strings.visitors, preview.totalVisitors, Color(0xFF38BDF8))
                SidebarMetric(HavnIconKey.Leads, strings.interestedBuyers, preview.interestedBuyers, Color(0xFF7DD36F))
                SidebarMetric(HavnIconKey.Alert, strings.hotLeads, preview.hotLeads, Color(0xFFFF6B4A))
                SidebarMetric(HavnIconKey.Check, strings.warmLeadsTitle, preview.warmLeads, Color(0xFFF6B84B))
                SidebarMetric(HavnIconKey.Check, strings.coldLeadsTitle, preview.coldLeads, theme.secondaryText)
                SidebarMetric(HavnIconKey.Calendar, strings.completedFollowUps, preview.followUpsSent, theme.primaryAccent)
            }
        }
        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(strings.reportHistory, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
                if (reportHistory.isEmpty()) {
                    Text(strings.noPreviousReportsYet, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                } else {
                    reportHistory.take(5).forEach { report ->
                        ReportHistoryButton(
                            report = report,
                            selected = report.id == selectedReportId,
                            onClick = { onReportHistoryClick(report.id) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun SellerReportPdfActions(
    preview: SellerReportPreview,
    generatedPdf: GeneratedSellerReportExport?,
    uploadedPdf: HavnAttachment?,
    isUploadingPdf: Boolean,
    shareError: String?,
    onGenerate: () -> Unit,
    onOpenPdf: () -> Unit,
    onSendEmail: () -> Unit,
    onSendSms: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(strings.actions, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            shareError?.let {
                Text(it, color = theme.danger, style = MaterialTheme.typography.bodyMedium)
            }
            generatedPdf?.let { pdf ->
                Text(
                    if (uploadedPdf != null) strings.pdfSavedToPropertyFiles.withPdfFile(pdf.fileName) else strings.pdfSavingToPropertyFiles.withPdfFile(pdf.fileName),
                    color = if (uploadedPdf != null) theme.success else theme.secondaryText,
                    style = MaterialTheme.typography.labelLarge
                )
            }
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val compact = maxWidth < 620.dp
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        HavnButton(
                            text = strings.generatePdf,
                            onClick = onGenerate,
                            loading = isUploadingPdf,
                            modifier = Modifier.fillMaxWidth(),
                            leadingIcon = HavnIconKey.Reports
                        )
                        HavnButton(
                            text = strings.previewPdf,
                            onClick = onOpenPdf,
                            enabled = uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.fillMaxWidth(),
                            leadingIcon = HavnIconKey.Search
                        )
                        HavnButton(
                            text = strings.sendViaEmail,
                            onClick = onSendEmail,
                            enabled = generatedPdf != null && uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.fillMaxWidth(),
                            leadingIcon = HavnIconKey.Mail
                        )
                        HavnButton(
                            text = strings.sendViaSms,
                            onClick = onSendSms,
                            enabled = generatedPdf != null && uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.fillMaxWidth(),
                            leadingIcon = HavnIconKey.Message
                        )
                    }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        HavnButton(
                            text = strings.generatePdf,
                            onClick = onGenerate,
                            loading = isUploadingPdf,
                            modifier = Modifier.weight(1f),
                            leadingIcon = HavnIconKey.Reports
                        )
                        HavnButton(
                            text = strings.previewPdf,
                            onClick = onOpenPdf,
                            enabled = uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.weight(1f),
                            leadingIcon = HavnIconKey.Search
                        )
                        HavnButton(
                            text = strings.sendViaEmail,
                            onClick = onSendEmail,
                            enabled = generatedPdf != null && uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.weight(1f),
                            leadingIcon = HavnIconKey.Mail
                        )
                        HavnButton(
                            text = strings.sendViaSms,
                            onClick = onSendSms,
                            enabled = generatedPdf != null && uploadedPdf != null,
                            style = HavnButtonStyle.Secondary,
                            modifier = Modifier.weight(1f),
                            leadingIcon = HavnIconKey.Message
                        )
                    }
                }
            }
            Text(
                strings.pdfIncludesSellerReport,
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelMedium
            )
        }
    }
}

@Composable
private fun ReportHistoryButton(
    report: SellerReport,
    selected: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        color = if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 10.dp, vertical = 9.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    report.createdAt.compactDateTimeLabel(),
                    color = if (selected) theme.primaryAccent else theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            ReportStatusBadge(report.status)
        }
    }
}

@Composable
private fun PropertyHeroPlaceholder(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier.background(
            Brush.linearGradient(
                listOf(Color(0xFF0F172A), Color(0xFF1D4ED8), Color(0xFFF59E0B))
            ),
            shape = MaterialTheme.shapes.medium
        )
    )
}

@Composable
private fun SmallPill(text: String) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.card, shape = MaterialTheme.shapes.small, border = BorderStroke(1.dp, theme.border)) {
        Text(text, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.padding(horizontal = 10.dp, vertical = 7.dp))
    }
}

@Composable
private fun ScoreBlock(score: Int, label: String, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Column(modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(score.toString(), color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
        Text("/100", color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        Text(label, color = theme.primaryAccent, style = MaterialTheme.typography.labelSmall, maxLines = 2, overflow = TextOverflow.Ellipsis)
    }
}

@Composable
private fun PropertyFunnelCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(strings.propertyLeadFunnel, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            FunnelStage(strings.visitors, preview.totalVisitors, Color(0xFF2563EB), 1f)
            FunnelStage(strings.interestedBuyers, preview.interestedBuyers, Color(0xFF10B981), 0.82f)
            FunnelStage(strings.hotLeads, preview.hotLeads, Color(0xFFF59E0B), 0.64f)
            FunnelStage(strings.tourSignals, preview.privateTours, Color(0xFF8B5CF6), 0.48f)
            FunnelStage(strings.offerSignals, preview.offers, Color(0xFFDB2777), 0.35f)
            Text("${strings.conversion}: ${preview.interestedBuyers.percentOf(preview.totalVisitors)} ${strings.interested}, ${preview.hotLeads.percentOf(preview.totalVisitors)} ${strings.hotLowercase}, ${preview.privateTours.percentOf(preview.totalVisitors)} ${strings.tourSignalsLowercase}.", color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun FunnelStage(label: String, value: Int, color: Color, fraction: Float) {
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        Surface(modifier = Modifier.fillMaxWidth(fraction).height(44.dp), color = color.copy(alpha = 0.82f), shape = MaterialTheme.shapes.small) {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                Text(value.toString(), color = Color.White, style = MaterialTheme.typography.titleMedium)
                Text(label, color = Color.White, style = MaterialTheme.typography.labelSmall)
            }
        }
    }
}

@Composable
private fun PropertyTimelineCard(timeline: List<PropertyReportOpenHouseInsight>, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.propertyTimeline, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val wide = maxWidth >= 620.dp
                if (wide) {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                        timeline.take(4).forEach { item ->
                            TimelineMiniCard(item, Modifier.weight(1f))
                        }
                    }
                } else {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        timeline.take(4).forEach { item -> TimelineMiniCard(item, Modifier.fillMaxWidth()) }
                    }
                }
            }
            Text(strings.timelineBasisNote, color = Color(0xFF4ADE80), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun TimelineMiniCard(item: PropertyReportOpenHouseInsight, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(item.date, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            Text(item.title, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text("${item.visitors} ${strings.visitorsLowercase} · ${item.hotLeads} ${strings.hotLeadsLowercase}", color = theme.primaryText, style = MaterialTheme.typography.labelSmall)
            ScoreBlock(item.sentimentScore, strings.sentiment)
        }
    }
}

@Composable
private fun OpenHouseBreakdownCard(timeline: List<PropertyReportOpenHouseInsight>) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(strings.openHouseBreakdown, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            timeline.forEach { item ->
                Surface(color = theme.surface, shape = MaterialTheme.shapes.medium, border = BorderStroke(1.dp, theme.border), modifier = Modifier.fillMaxWidth()) {
                    Column(Modifier.padding(10.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            Text(item.title, color = theme.primaryText, style = MaterialTheme.typography.labelLarge, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                            Text(item.date, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge)
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                            ReportMetaPill("${item.visitors} ${strings.visitorsLowercase}")
                            ReportMetaPill("${item.interestedBuyers} ${strings.interestedLowercase}")
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                            ReportMetaPill("${item.hotLeads} ${strings.hotLowercase}")
                            ReportMetaPill("${item.privateTours} ${strings.toursLowercase}")
                            ReportMetaPill("${item.sentimentScore}% ${strings.sentiment.lowercase()}")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FeatureScoresCard(title: String, scores: List<PropertyReportFeatureInterest>, modifier: Modifier, accent: Color) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(title.uppercase(), color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            if (scores.isEmpty()) {
                Text(
                    LocalHavnStrings.current.noRepeatedFeedbackMentions,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyMedium
                )
            } else {
                scores.take(5).forEach { score ->
                    ScoreLine(score.label, score.score, accent)
                }
            }
            Text(LocalHavnStrings.current.scoresMentionRates, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        }
    }
}

@Composable
private fun ScoreLine(label: String, value: Int, accent: Color) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
            Text("$value%", color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
        }
        Surface(modifier = Modifier.fillMaxWidth().height(4.dp), color = theme.border.copy(alpha = 0.45f), shape = MaterialTheme.shapes.small) {
            Box(Modifier.fillMaxWidth(value / 100f).fillMaxSize().background(accent))
        }
    }
}

@Composable
private fun AiBriefingCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                HavnIcon(HavnIconKey.Star, "AI", tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                Text(strings.aiExecutiveBriefing, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            Text(preview.buyerSentimentSummary, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
            Text(strings.viewAllAiInsights, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
private fun AiInsightsJsonCard(
    preview: SellerReportPreview,
    onGenerate: () -> Unit,
    isLoading: Boolean,
    modifier: Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val language = LocalHavnLanguage.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                HavnIcon(HavnIconKey.Star, "AI", tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                Text(strings.aiInsights, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            }
            if (preview.aiInsightsJson.isNotBlank()) {
                AiOutputSections(
                    jsonText = preview.aiInsightsJson,
                    keyOrder = sellerReportAiOutputOrder,
                    titleForKey = { key -> key.sellerReportAiSectionTitle(language) }
                )
            }
            HavnButton(
                if (isLoading) language.generatingSellerReportInsightsLabel() else strings.generateAiInsights,
                onClick = onGenerate,
                loading = isLoading,
                showLoadingText = true,
                style = HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Star
            )
        }
    }
}

private fun HavnLanguage.generatingSellerReportInsightsLabel(): String = when (this) {
    HavnLanguage.Slovak -> "Generujem..."
    else -> "Generating..."
}

private val sellerReportAiOutputOrder = listOf(
    "executive_summary",
    "buyer_sentiment_summary",
    "momentum_analysis",
    "property_strengths",
    "buyer_concerns",
    "agent_activity_summary",
    "recommendations",
    "next_steps",
    "seller_confidence_score"
)

private val hiddenSellerReportAiOutputKeys = setOf(
    "language",
    "output_required",
    "property",
    "report_period",
    "open_houses",
    "totals",
    "agent_notes"
)

private fun String.normalizedAiOutputKey(): String =
    trim()
        .lowercase()
        .replace("-", "_")
        .replace(" ", "_")

@Composable
private fun AiOutputSections(
    jsonText: String,
    keyOrder: List<String>,
    titleForKey: (String) -> String
) {
    val theme = LocalHavnThemeTokens.current
    val sections = remember(jsonText, titleForKey) { parseAiOutputSections(jsonText, keyOrder, titleForKey) }
    if (sections.isNotEmpty()) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            sections.forEach { section ->
                Surface(
                    color = theme.background.copy(alpha = 0.35f),
                    shape = MaterialTheme.shapes.medium,
                    border = BorderStroke(1.dp, theme.border),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(section.title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                        Text(section.body, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
        }
    }
}

private data class AiOutputSection(val title: String, val body: String)

private fun parseAiOutputSections(
    jsonText: String,
    keyOrder: List<String>,
    titleForKey: (String) -> String
): List<AiOutputSection> {
    val jsonObject = runCatching { Json.parseToJsonElement(jsonText).jsonObject }.getOrNull() ?: return emptyList()
    val orderedKeys = keyOrder
        .filter { it in jsonObject.keys }
        .filterNot { it.normalizedAiOutputKey() in hiddenSellerReportAiOutputKeys }
    return orderedKeys.mapNotNull { key ->
        val value = jsonObject[key] ?: return@mapNotNull null
        val body = value.aiTextValue(titleForKey).takeIf { it.isNotBlank() } ?: return@mapNotNull null
        AiOutputSection(titleForKey(key), body)
    }
}

private fun JsonElement.aiTextValue(titleForKey: (String) -> String): String = when (this) {
    is JsonPrimitive -> contentOrNull ?: toString()
    is JsonArray -> mapIndexed { index, item ->
        val value = item.aiTextValue(titleForKey)
        if (value.contains('\n')) "${index + 1}. $value" else "${index + 1}. $value"
    }.joinToString("\n")
    is JsonObject -> entries.joinToString("\n") { (key, value) ->
        "${titleForKey(key)}: ${value.aiTextValue(titleForKey)}"
    }
}

private fun String.defaultAiTitle(): String =
    split("_", "-")
        .filter { it.isNotBlank() }
        .joinToString(" ") { part -> part.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() } }

private fun String.sellerReportAiSectionTitle(language: HavnLanguage): String = when (language) {
    HavnLanguage.Slovak -> when (this) {
        "executive_summary" -> "Výkonný súhrn"
        "buyer_sentiment_summary" -> "Sentiment kupujúcich"
        "momentum_analysis" -> "Analýza dynamiky"
        "property_strengths" -> "Silné stránky nehnuteľnosti"
        "buyer_concerns" -> "Obavy kupujúcich"
        "agent_activity_summary" -> "Aktivita agenta"
        "recommendations" -> "Odporúčania"
        "next_steps" -> "Ďalšie kroky"
        "seller_confidence_score" -> "Skóre dôvery predávajúceho"
        else -> defaultAiTitle()
    }
    else -> defaultAiTitle()
}

private fun HavnLanguage.aiLanguageInstruction(): String = when (this) {
    HavnLanguage.Slovak -> "Slovak (Slovenčina, language code sk). Important: use Slovak, not Slovenian."
    HavnLanguage.Czech -> "Czech (Čeština, language code cs)."
    HavnLanguage.German -> "German (Deutsch, language code de)."
    HavnLanguage.French -> "French (Français, language code fr)."
    HavnLanguage.Spanish -> "Spanish (Español, language code es)."
    HavnLanguage.Italian -> "Italian (Italiano, language code it)."
    HavnLanguage.Croatian -> "Croatian (Hrvatski, language code hr)."
    HavnLanguage.Portuguese -> "Portuguese (Português, language code pt)."
    HavnLanguage.Dutch -> "Dutch (Nederlands, language code nl)."
    HavnLanguage.Polish -> "Polish (Polski, language code pl)."
    HavnLanguage.Hungarian -> "Hungarian (Magyar, language code hu)."
    HavnLanguage.Romanian -> "Romanian (Română, language code ro)."
    HavnLanguage.English -> "English (language code en)."
}

@Composable
private fun AgentActivityCard(preview: SellerReportPreview, modifier: Modifier) {
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(9.dp)) {
            Text(strings.agentActivity, color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleMedium)
            ActivityLine(strings.followUpsSentTitle, preview.followUpsSent)
            ActivityLine(strings.tourSignals, preview.privateTours)
            ActivityLine(strings.financingPacketsShared, preview.financingInfoShared)
            ActivityLine(strings.buyerCalls, preview.callsMade)
        }
    }
}

@Composable
private fun ActivityLine(label: String, value: Int) {
    val theme = LocalHavnThemeTokens.current
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
        Text(value.toString(), color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun RecommendationsCard(preview: SellerReportPreview, onChange: (String) -> Unit, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text(strings.aiRecommendations, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            HavnTextField(
                value = preview.recommendations,
                onValueChange = onChange,
                label = strings.recommendations,
                placeholder = strings.recommendations,
                singleLine = false,
                leadingIcon = HavnIconKey.Star
            )
        }
    }
}

@Composable
private fun PropertyEvidenceCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(9.dp)) {
            Text(strings.propertyDataEvidence, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(preview.marketPositionComparison, color = theme.primaryText, style = MaterialTheme.typography.bodyMedium)
            EvidenceLine(strings.attendanceCoverage, "${preview.totalVisitors} ${strings.visitorsLowercase} / ${preview.openHouseCount} ${strings.connectedOpeningPlural}")
            EvidenceLine(strings.leadQuality, "${preview.hotLeads} ${strings.commonHot.lowercase()}, ${preview.warmLeads} ${strings.commonWarm.lowercase()}, ${preview.coldLeads} ${strings.commonCold.lowercase()}")
            EvidenceLine(strings.followUpExecution, "${preview.followUpsSent} ${strings.completedFollowUps.lowercase()}")
            EvidenceLine(strings.activityChannels, "${preview.callsMade} ${strings.callsLowercase} · ${preview.smsSent} SMS · ${preview.emailsSent} ${strings.emailsLowercase}")
        }
    }
}

@Composable
private fun EvidenceLine(label: String, value: String) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelSmall)
        Text(value, color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun OfferReadinessCard(preview: SellerReportPreview, modifier: Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text(strings.offerReadinessScore, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text("${preview.offerReadinessScore}%", color = theme.primaryText, style = MaterialTheme.typography.headlineMedium)
            Text(preview.offerReadinessExplanation, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun SidebarMetric(icon: HavnIconKey, label: String, value: Int, accent: Color) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HavnIcon(icon, contentDescription = label, tint = accent, modifier = Modifier.size(18.dp))
        Text(label, color = theme.secondaryText, style = MaterialTheme.typography.labelLarge, modifier = Modifier.weight(1f))
        Text(value.toString(), color = theme.primaryText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun ReportSection(title: String, body: String) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(5.dp))
        Text(body, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
private fun EditableReportSection(title: String, body: String, onChange: (String) -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(title, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(5.dp))
        HavnTextField(
            value = body,
            onValueChange = onChange,
            label = title,
            placeholder = title,
            singleLine = false,
            leadingIcon = HavnIconKey.Reports
        )
    }
}

@Composable
private fun OpenHousePickerRow(
    openHouse: OpenHouse,
    selected: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    HavnButton(
        text = "${openHouse.propertyTitle} · ${openHouse.startsAt.compactDateTime(strings)}",
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Calendar
    )
}

@Composable
private fun ReportFilters(
    selected: ReportStatusFilter,
    reports: List<SellerReport>,
    onSelected: (ReportStatusFilter) -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
        visibleReportStatusFilters.forEach { filter ->
            ReportFilterChip(
                label = filter.localizedLabel(strings),
                selected = selected == filter,
                onClick = { onSelected(filter) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun ReportFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    HavnListFilterChip(
        label = label,
        selected = selected,
        onClick = onClick,
        modifier = modifier
    )
}

@Composable
private fun SavedReportRow(report: SellerReport, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(112.dp),
            verticalAlignment = Alignment.Top
        ) {
            Box(
                modifier = Modifier
                    .width(124.dp)
                    .fillMaxHeight()
                    .clip(RoundedCornerShape(topStart = 24.dp, bottomStart = 24.dp))
            ) {
                ReportPropertyPhoto(
                    dataUrl = report.propertyThumbnailDataUrl,
                    modifier = Modifier.fillMaxSize(),
                    loadPreview = false
                )
                ReportStatusBadge(
                    status = report.status,
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(7.dp)
                )
            }
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .padding(start = 12.dp, top = 10.dp, end = 10.dp, bottom = 10.dp),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(
                        report.propertyTitle?.takeIf { it.isNotBlank() } ?: strings.reportPreview,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Text(
                        report.propertyAddress?.takeIf { it.isNotBlank() } ?: report.summary?.take(80) ?: strings.openHouseReport,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ReportMetricItem(
                        icon = HavnIconKey.Users,
                        value = report.visitorCount.toString(),
                        label = strings.visitors,
                        modifier = Modifier.weight(1f)
                    )
                    ReportMetricItem(
                        icon = HavnIconKey.Leads,
                        value = report.hotCount.toString(),
                        label = strings.hotLeads,
                        modifier = Modifier.weight(1f)
                    )
                    ReportMetricItem(
                        icon = HavnIconKey.Calendar,
                        value = report.createdAt.compactDateLabel(),
                        label = strings.date,
                        modifier = Modifier.weight(1.15f)
                    )
                }
            }
        }
    }
}

@Composable
private fun ReportPropertyPhoto(
    dataUrl: String?,
    modifier: Modifier = Modifier,
    loadPreview: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    Box(modifier = modifier) {
        if (!dataUrl.isNullOrBlank()) {
            if (loadPreview) {
                HavnAsyncImage(
                    dataUrl = havnPropertyPhotoPreviewSource(dataUrl, 180) ?: dataUrl,
                    contentDescription = LocalHavnStrings.current.propertyPhotoContentDescription,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
            } else {
                HavnDatabaseImage(
                    dataUrl = dataUrl,
                    previewDataUrl = null,
                    contentDescription = LocalHavnStrings.current.propertyPhotoContentDescription,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
            }
        } else {
            Box(contentAlignment = Alignment.Center) {
                HavnSkeletonBlock(Modifier.fillMaxSize(), shape = MaterialTheme.shapes.medium)
                HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(26.dp))
            }
        }
    }
}

@Composable
private fun ReportStatusBadge(status: SellerReportStatus, modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val background = when (status) {
        SellerReportStatus.Draft -> theme.primaryAccent.copy(alpha = 0.86f)
        SellerReportStatus.Accepted -> Color(0xFF22C55E)
        SellerReportStatus.Archived -> theme.secondaryText.copy(alpha = 0.86f)
    }
    val contentColor = background.readableBadgeTextColor()
    val labelStyle = MaterialTheme.typography.labelLarge.copy(
        fontSize = when {
            status.localizedLabel(strings).length > 10 -> 9.sp
            status.localizedLabel(strings).length > 8 -> 10.sp
            else -> MaterialTheme.typography.labelLarge.fontSize
        }
    )
    Surface(
        modifier = modifier.widthIn(max = 82.dp),
        color = background,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, background.copy(alpha = 0.95f))
    ) {
        Text(
            status.localizedLabel(strings),
            color = contentColor,
            style = labelStyle,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ReportMetricItem(
    icon: HavnIconKey,
    value: String,
    label: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(3.dp)
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(5.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(
                icon = icon,
                contentDescription = null,
                modifier = Modifier.size(14.dp),
                tint = theme.secondaryText
            )
            Text(
                value,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            label,
            color = theme.mutedText,
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun ReportMetaPill(text: String) {
    val theme = LocalHavnThemeTokens.current
    Surface(color = theme.surface, shape = MaterialTheme.shapes.small, border = BorderStroke(1.dp, theme.border)) {
        Text(
            text,
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelLarge,
            modifier = Modifier.padding(horizontal = 9.dp, vertical = 5.dp),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private fun String?.compactDateLabel(): String {
    if (this.isNullOrBlank()) return "-"
    val date = substringBefore("T").takeIf { it.isNotBlank() } ?: return "-"
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = when (parts[1]) {
        "01" -> "Jan"
        "02" -> "Feb"
        "03" -> "Mar"
        "04" -> "Apr"
        "05" -> "May"
        "06" -> "Jun"
        "07" -> "Jul"
        "08" -> "Aug"
        "09" -> "Sep"
        "10" -> "Oct"
        "11" -> "Nov"
        else -> "Dec"
            }
    return "$month ${parts[2].trimStart('0').ifBlank { "1" }}"
}

private fun String?.compactDateTimeLabel(): String {
    if (this.isNullOrBlank()) return "just now"
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").compactDateLabel()
    val time = clean.substringAfter(" ", "").readableReportTime()
    return listOfNotNull(date.takeIf { it != "-" }, time).joinToString(" at ").ifBlank { clean }
}

private fun Int.percentOf(total: Int): String {
    if (total <= 0) return "0%"
    return "${((this.toDouble() / total.toDouble()) * 100.0).toInt().coerceIn(0, 100)}%"
}

private fun String.shortConfidenceLabel(): String = when {
    contains("Strong", ignoreCase = true) -> "Strong"
    contains("Moderate", ignoreCase = true) -> "Moderate"
    contains("Momentum", ignoreCase = true) -> "Needs Momentum"
    isBlank() -> "Draft"
    else -> this
}

private fun Color.readableBadgeTextColor(): Color {
    val luminance = (0.299f * red) + (0.587f * green) + (0.114f * blue)
    return if (luminance > 0.58f) Color.Black else Color.White
}

private data class GeneratedSellerReportExport(
    val fileName: String,
    val bytes: ByteArray
)

private fun Set<String>.toggleReportRecipient(value: String): Set<String> =
    if (value in this) this - value else this + value

private fun SellerReportPreview.generateSellerReportXlsx(
    agentName: String?,
    agencyName: String?,
    strings: HavnStrings,
    language: HavnLanguage
): GeneratedSellerReportExport {
    val fileName = "HAVN-Seller-Report-${propertyTitle.ifBlank { propertyAddress }.pdfSafeName()}.xlsx"
    val labels = SellerReportPdfLabels.forLanguage(language)
    val aiRows = if (aiInsightsJson.isBlank()) {
        listOf(listOf(strings.aiInsights, labels.noAiInsights))
    } else {
        val aiSections = parseAiOutputSections(
            jsonText = aiInsightsJson,
            keyOrder = sellerReportAiOutputOrder,
            titleForKey = { it.defaultAiTitle() }
        )
        if (aiSections.isEmpty()) {
            listOf(listOf(strings.aiInsights, aiInsightsJson))
        } else {
            listOf(listOf("Section", "Insight")) + aiSections.map { listOf(it.title, it.body) }
        }
    }
    val sheets = listOf(
        HavnXlsxSheet(
            strings.reportPreview,
            listOf(
                listOf("HAVN", strings.reportPreview),
                listOf(labels.generated, reportGeneratedAt.compactDateTimeLabel()),
                listOf(labels.agent, agentName?.takeIf { it.isNotBlank() } ?: labels.notAvailable),
                listOf(labels.agency, agencyName?.takeIf { it.isNotBlank() } ?: "HAVN")
            )
        ),
        HavnXlsxSheet(
            labels.property,
            listOf(
                listOf(labels.property, propertyTitle.ifBlank { propertyAddress }),
                listOf(strings.commonAddress, propertyAddress),
                listOf(labels.price, propertyPrice.ifBlank { labels.notAvailable }),
                listOf(labels.status, propertyStatus.ifBlank { labels.notAvailable }),
                listOf(labels.daysActive, daysActive),
                listOf(labels.reportPeriod, reportPeriod.ifBlank { labels.notAvailable })
            )
        ),
        HavnXlsxSheet(
            labels.performanceMetrics,
            listOf(
                listOf(strings.connectedOpenings, openHouseCount),
                listOf(strings.visitors, totalVisitors),
                listOf(strings.interestedBuyers, interestedBuyers),
                listOf(strings.hotLeads, hotLeads),
                listOf(strings.warmLeadsTitle, warmLeads),
                listOf(strings.coldLeadsTitle, coldLeads),
                listOf(strings.tourSignals, privateTours),
                listOf(strings.offerSignals, offers),
                listOf(strings.completedFollowUps, followUpsSent),
                listOf(labels.sellerConfidence, sellerConfidenceScore),
                listOf(strings.sellerConfidenceExplanation, sellerConfidenceExplanation.takeIf { it.isNotBlank() } ?: labels.confidenceNotAvailable)
            )
        ),
        HavnXlsxSheet(
            strings.propertyLeadFunnel,
            listOf(listOf("Stage", "Count", "Percent of visitors")) + listOf(
                listOf(strings.visitors, totalVisitors, "100%"),
                listOf(strings.interestedBuyers, interestedBuyers, interestedBuyers.percentOf(totalVisitors)),
                listOf(strings.hotLeads, hotLeads, hotLeads.percentOf(totalVisitors)),
                listOf(strings.tourSignals, privateTours, privateTours.percentOf(totalVisitors)),
                listOf(strings.offerSignals, offers, offers.percentOf(totalVisitors))
            )
        ),
        HavnXlsxSheet(
            labels.openHouseTimeline,
            listOf(listOf(strings.date, "Time", "Title", strings.visitors, strings.interestedBuyers, strings.hotLeads, strings.tourSignals, strings.followUpsSent, labels.sentiment)) +
                timeline.map { item ->
                    listOf(item.date, item.time, item.title, item.visitors, item.interestedBuyers, item.hotLeads, item.privateTours, item.followUpsSent, item.sentimentScore)
                }
        ),
        HavnXlsxSheet(strings.aiInsights, aiRows),
        HavnXlsxSheet(labels.footer, listOf(listOf(labels.reviewBeforeSending)))
    )
    return GeneratedSellerReportExport(fileName, buildHavnXlsx(sheets))
}

private fun buildA4SellerReportPdf(lines: List<String>, strings: HavnStrings, labels: SellerReportPdfLabels): ByteArray {
    val usableLinesPerPage = 42
    val pages = lines.chunked(usableLinesPerPage).ifEmpty { listOf(listOf("HAVN ${strings.reportPreview}")) }
    val objects = mutableListOf<String>()
    fun addObject(value: String): Int {
        objects += value
        return objects.size
    }

    val catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>")
    val pagesId = addObject("")
    val fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding << /Type /Encoding /BaseEncoding /WinAnsiEncoding /Differences [${sellerReportPdfEncodingDifferences()}] >> >>")
    val pageIds = mutableListOf<Int>()
    pages.forEachIndexed { pageIndex, pageLines ->
        val content = buildString {
            append("q\n")
            append("BT\n")
            append("/F1 16 Tf\n")
            append("0.74 0.52 0.18 rg\n")
            append("54 800 Td\n")
            append("<${"HAVN".sellerReportPdfTextHex()}> Tj\n")
            append("/F1 10 Tf\n")
            append("0.85 0.87 0.90 rg\n")
            append("0 -18 Td\n")
            append("<${"${strings.reportPreview} - ${labels.page} ${pageIndex + 1} ${labels.of} ${pages.size}".sellerReportPdfTextHex()}> Tj\n")
            append("/F1 10 Tf\n")
            append("0.06 0.07 0.09 rg\n")
            append("0 -26 Td\n")
            pageLines.forEach { rawLine ->
                val chunks = rawLine.pdfLineChunks()
                if (chunks.isEmpty()) {
                    append("0 -12 Td\n")
                } else {
                    chunks.forEach { chunk ->
                        append("<")
                        append(chunk.sellerReportPdfTextHex())
                        append("> Tj\n")
                        append("0 -14 Td\n")
                    }
                }
            }
            append("/F1 8 Tf\n")
            append("0.45 0.45 0.45 rg\n")
            append("0 -12 Td\n")
            append("<${"HAVN Real Estate App - Agent reviewed seller briefing".sellerReportPdfTextHex()}> Tj\n")
            append("ET\n")
            append("Q\n")
        }
        val contentId = addObject("<< /Length ${content.encodeToByteArray().size} >>\nstream\n$content\nendstream")
        val pageId = addObject("<< /Type /Page /Parent $pagesId 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 $fontId 0 R >> >> /Contents $contentId 0 R >>")
        pageIds += pageId
    }
    objects[pagesId - 1] = "<< /Type /Pages /Kids [${pageIds.joinToString(" ") { "$it 0 R" }}] /Count ${pageIds.size} >>"

    val pdf = StringBuilder("%PDF-1.4\n")
    val offsets = mutableListOf(0)
    objects.forEachIndexed { index, obj ->
        offsets += pdf.length
        pdf.append(index + 1).append(" 0 obj\n")
        pdf.append(obj).append("\n")
        pdf.append("endobj\n")
    }
    val xrefOffset = pdf.length
    pdf.append("xref\n")
    pdf.append("0 ").append(objects.size + 1).append("\n")
    pdf.append("0000000000 65535 f \n")
    offsets.drop(1).forEach { offset ->
        pdf.append(offset.toString().padStart(10, '0')).append(" 00000 n \n")
    }
    pdf.append("trailer\n")
    pdf.append("<< /Size ").append(objects.size + 1).append(" /Root $catalogId 0 R >>\n")
    pdf.append("startxref\n")
    pdf.append(xrefOffset).append("\n")
    pdf.append("%%EOF")
    return pdf.toString().encodeToByteArray()
}

private fun String.pdfLineChunks(maxLength: Int = 86): List<String> {
    if (isBlank()) return emptyList()
    val words = split(Regex("\\s+")).filter { it.isNotBlank() }
    val lines = mutableListOf<String>()
    var current = StringBuilder()
    words.forEach { word ->
        if (current.isEmpty()) {
            current.append(word)
        } else if (current.length + 1 + word.length <= maxLength) {
            current.append(' ').append(word)
        } else {
            lines += current.toString()
            current = StringBuilder(word)
        }
    }
    if (current.isNotEmpty()) lines += current.toString()
    return lines
}

private val sellerReportPdfGlyphBytes: Map<Char, Pair<Int, String>> = listOf(
    'Á' to (128 to "Aacute"),
    'á' to (129 to "aacute"),
    'Ä' to (130 to "Adieresis"),
    'ä' to (131 to "adieresis"),
    'Č' to (132 to "Ccaron"),
    'č' to (133 to "ccaron"),
    'Ď' to (134 to "Dcaron"),
    'ď' to (135 to "dcaron"),
    'É' to (136 to "Eacute"),
    'é' to (137 to "eacute"),
    'Í' to (138 to "Iacute"),
    'í' to (139 to "iacute"),
    'Ĺ' to (140 to "Lacute"),
    'ĺ' to (141 to "lacute"),
    'Ľ' to (142 to "Lcaron"),
    'ľ' to (143 to "lcaron"),
    'Ň' to (144 to "Ncaron"),
    'ň' to (145 to "ncaron"),
    'Ó' to (146 to "Oacute"),
    'ó' to (147 to "oacute"),
    'Ô' to (148 to "Ocircumflex"),
    'ô' to (149 to "ocircumflex"),
    'Ŕ' to (150 to "Racute"),
    'ŕ' to (151 to "racute"),
    'Š' to (152 to "Scaron"),
    'š' to (153 to "scaron"),
    'Ť' to (154 to "Tcaron"),
    'ť' to (155 to "tcaron"),
    'Ú' to (156 to "Uacute"),
    'ú' to (157 to "uacute"),
    'Ý' to (158 to "Yacute"),
    'ý' to (159 to "yacute"),
    'Ž' to (160 to "Zcaron"),
    'ž' to (161 to "zcaron")
).associate { it.first to it.second }

private fun sellerReportPdfEncodingDifferences(): String =
    sellerReportPdfGlyphBytes.values
        .sortedBy { it.first }
        .joinToString(" ") { (byte, glyph) -> "$byte /$glyph" }

private fun String.sellerReportPdfTextHex(): String =
    flatMap { char ->
        val byte = when {
            char.code in 32..126 -> char.code
            char == '\n' || char == '\r' || char == '\t' -> 32
            char == '–' || char == '—' -> '-'.code
            char == '“' || char == '”' -> '"'.code
            char == '‘' || char == '’' -> '\''.code
            char == '€' -> 'E'.code
            else -> sellerReportPdfGlyphBytes[char]?.first ?: '?'.code
        }
        listOf(byte.toString(16).padStart(2, '0').uppercase())
    }.joinToString("")

private fun String.pdfSafeName(): String =
    trim()
        .ifBlank { "seller-report" }
        .replace(Regex("[^A-Za-z0-9._-]+"), "-")
        .trim('-')
        .take(80)
        .ifBlank { "seller-report" }

private fun String.withPdfFile(fileName: String): String =
    replace("%s", fileName)

private data class SellerReportPdfLabels(
    val generated: String,
    val agent: String,
    val agency: String,
    val property: String,
    val price: String,
    val status: String,
    val daysActive: String,
    val reportPeriod: String,
    val performanceMetrics: String,
    val sellerConfidence: String,
    val confidenceNotAvailable: String,
    val openHouseTimeline: String,
    val noOpenHouses: String,
    val sentiment: String,
    val noAiInsights: String,
    val footer: String,
    val reviewBeforeSending: String,
    val notAvailable: String,
    val page: String,
    val of: String
) {
    companion object {
        fun forLanguage(language: HavnLanguage): SellerReportPdfLabels = when (language) {
            HavnLanguage.Slovak -> SellerReportPdfLabels(
                generated = "Vygenerované",
                agent = "Agent",
                agency = "Agentúra",
                property = "Nehnuteľnosť",
                price = "Cena",
                status = "Stav",
                daysActive = "Dní aktívne",
                reportPeriod = "Obdobie reportu",
                performanceMetrics = "Výkonnostné metriky",
                sellerConfidence = "Dôvera predávajúceho",
                confidenceNotAvailable = "Vysvetlenie dôvery predávajúceho nie je dostupné.",
                openHouseTimeline = "Časová os obhliadok",
                noOpenHouses = "V čase reportu neboli dostupné žiadne pripojené obhliadky.",
                sentiment = "Sentiment",
                noAiInsights = "AI poznatky ešte neboli vygenerované.",
                footer = "Pätička HAVN",
                reviewBeforeSending = "AI generovaný obsah reportu je návrh. Agent ho musí pred odoslaním skontrolovať.",
                notAvailable = "Nie je dostupné",
                page = "strana",
                of = "z"
            )
            else -> SellerReportPdfLabels(
                generated = "Generated",
                agent = "Agent",
                agency = "Agency",
                property = "Property",
                price = "Price",
                status = "Status",
                daysActive = "Days active",
                reportPeriod = "Report period",
                performanceMetrics = "Performance Metrics",
                sellerConfidence = "Seller confidence",
                confidenceNotAvailable = "Seller confidence explanation is not available.",
                openHouseTimeline = "Open House Timeline",
                noOpenHouses = "No connected open houses were available at report time.",
                sentiment = "Sentiment",
                noAiInsights = "AI insights have not been generated yet.",
                footer = "HAVN footer",
                reviewBeforeSending = "AI-generated report content is a draft. Agent must review before sending.",
                notAvailable = "Not available",
                page = "Page",
                of = "of"
            )
        }
    }
}

private enum class ReportStatusFilter {
    All,
    Draft,
    Accepted,
    Archived;

    fun matches(report: SellerReport): Boolean = when (this) {
        All -> true
        Draft -> report.status == SellerReportStatus.Draft
        Accepted -> report.status == SellerReportStatus.Accepted
        Archived -> report.status == SellerReportStatus.Archived
    }

    fun countFor(reports: List<SellerReport>): Int = reports.count { matches(it) }
}

private val visibleSellerReportStatuses = listOf(
    SellerReportStatus.Draft,
    SellerReportStatus.Accepted
)

private val visibleReportStatusFilters = listOf(
    ReportStatusFilter.All,
    ReportStatusFilter.Draft,
    ReportStatusFilter.Accepted
)

private fun SellerReportStatus.localizedLabel(strings: HavnStrings): String = when (this) {
    SellerReportStatus.Draft -> strings.commonDraft
    SellerReportStatus.Accepted -> strings.commonAccepted
    SellerReportStatus.Archived -> strings.commonArchived
}

private fun ReportStatusFilter.localizedLabel(strings: HavnStrings): String = when (this) {
    ReportStatusFilter.All -> strings.commonAll
    ReportStatusFilter.Draft -> strings.commonDraft
    ReportStatusFilter.Accepted -> strings.commonAccepted
    ReportStatusFilter.Archived -> strings.commonArchived
}

private fun SellerReport.matchesSearch(query: String): Boolean {
    val normalized = query.trim()
    if (normalized.isBlank()) return true
    return listOfNotNull(
        propertyTitle,
        propertyAddress,
        status.label,
        summary,
        sellerFeedback,
        nextSteps,
        createdAt
    ).any { it.contains(normalized, ignoreCase = true) }
}

private fun String.compactDateTime(strings: com.havn.app.core.localization.HavnStrings): String {
    if (isBlank()) return strings.commonNotScheduled
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").compactDateLabel()
    val time = clean.substringAfter(" ", "").readableReportTime()
    return listOfNotNull(date.takeIf { it != "-" }, time).joinToString(" · ").ifBlank { clean }
}

private fun String.readableReportTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private fun SellerReportsMode.navigationDepth(): Int = when (this) {
    SellerReportsMode.List -> 0
    SellerReportsMode.Generate -> 1
    SellerReportsMode.Preview -> 2
}
